import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router'
import {
  FINDING_REFERENCE, getFindings, REFERENCE_LENGTH, referenceOf, type FindingSummary,
} from '../lib/api'

/** How many suggestions the box shows. The full list is one Enter away. */
const SHOWN = 8

/** How long a loaded list is reused before the box asks again. */
const FRESH_FOR_MS = 60_000

/**
 * The header's smart search (owner, 2026-09-25: "should be refined to smart search").
 *
 * It used to open ONE finding by its reference and refuse everything else -- and references moved
 * off the list rows, so nobody had one to type. Now a person types what they are looking for: the box
 * suggests matching open findings by title (and by the plain summary line), arrow keys and Enter open
 * one, Enter on anything else opens the full list already filtered, and a pasted reference still goes
 * straight to its finding.
 *
 * Suggestions come from the open findings -- the hundred most severe, one request -- loaded when the
 * box is first used and reused for a minute, and filtered in the browser, so typing never waits on
 * the network. They are suggestions, not the answer: "See all results" searches the whole list. Only
 * titles and summary lines are matched -- the text the list already shows -- never evidence.
 */
export function HeaderSearch({ workspaceId }: {
  /**
   * The workspace the shell says this session is in, or null while unknown. Suggestions are shown
   * only from a list that answered for this same workspace: another tab signing into a different one
   * replaces the shared cookie, and its findings must not appear under this workspace's name (the
   * rule every live page keeps with agrees()).
   */
  workspaceId: string | null
}) {
  const navigate = useNavigate()
  const listId = useId()
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState(false)
  const [active, setActive] = useState(-1)
  const [loaded, setLoaded] = useState<{ workspaceId: string; items: FindingSummary[] } | null>(null)
  const [failed, setFailed] = useState(false)
  // When the box may ask again: a minute after an answer, ten seconds after a failure -- not on every
  // keystroke while the server is refusing.
  const nextAsk = useRef(0)
  const loading = useRef(false)

  // A different workspace is a different list: ask again rather than reuse the last one.
  useEffect(() => {
    nextAsk.current = 0
  }, [workspaceId])

  async function load() {
    if (loading.current || Date.now() < nextAsk.current) return
    loading.current = true
    const result = await getFindings({ status: 'active', sort: 'severity', pageSize: 100 })
    loading.current = false
    if (result.ok && result.data?.items) {
      setLoaded({ workspaceId: result.data.workspaceId, items: result.data.items })
      setFailed(false)
      nextAsk.current = Date.now() + FRESH_FOR_MS
    } else {
      setFailed(true)
      nextAsk.current = Date.now() + 10_000
    }
  }

  // Only a list for THIS session's workspace suggests anything; one for another is treated as none.
  const findings = loaded && workspaceId !== null && loaded.workspaceId === workspaceId ? loaded.items : null
  const unavailable = failed || (loaded !== null && findings === null)

  const term = query.trim().toLowerCase()
  // A pasted reference or whole id also suggests the finding it names: the start of its id.
  const reference = FINDING_REFERENCE.test(term) ? term.replace(/-/g, '') : null

  const byText = (f: FindingSummary) =>
    f.title.toLowerCase().includes(term) || (f.summary ?? '').toLowerCase().includes(term)

  const matches =
    term.length < 2 || !findings
      ? []
      : findings
          .filter((f) =>
            byText(f)
            || (reference !== null && f.id.replace(/-/g, '').toLowerCase().startsWith(reference)))
          .slice(0, SHOWN)

  // Suggestions, then one last option that always exists once something is typed: the full list.
  const options = term.length >= 2 ? matches.length + 1 : 0
  const open = expanded && options > 0

  useEffect(() => {
    setActive(-1)
  }, [query])

  function openFinding(finding: FindingSummary) {
    close()
    navigate(`/findings/${referenceOf(finding.id)}`)
  }

  function openAll() {
    const value = query.trim()
    close()
    navigate(`/recommendations?q=${encodeURIComponent(value)}`)
  }

  function close() {
    setQuery('')
    setExpanded(false)
    setActive(-1)
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    const value = query.trim()
    if (value === '') return

    // The highlighted option -- only while the list is open. After Escape or a blur the highlight
    // is not on screen, and Enter must not act on something the person can no longer see.
    if (open && active >= 0 && active < matches.length) return openFinding(matches[active]!)
    if (open && active === matches.length) return openAll()

    // A pasted reference goes straight to its finding, as it always did -- including one that is no
    // longer open, which no list shows. Only something as long as a printed reference, though, and
    // only when no title says it: "cafe", "2024" or "added" are hex-shaped words people search for.
    const reference = value.replace(/-/g, '')
    if (
      FINDING_REFERENCE.test(value)
      && reference.length >= REFERENCE_LENGTH
      && !(findings ?? []).some(byText)
    ) {
      close()
      navigate(`/findings/${value.toLowerCase()}`)
      return
    }

    openAll()
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' && options > 0) {
      event.preventDefault()
      setExpanded(true)
      setActive((i) => (i + 1) % options)
    } else if (event.key === 'ArrowUp' && options > 0) {
      event.preventDefault()
      setExpanded(true)
      setActive((i) => (i <= 0 ? options - 1 : i - 1))
    } else if (event.key === 'Escape') {
      setExpanded(false)
      setActive(-1)
    }
  }

  const optionId = (i: number) => `${listId}-option-${i}`

  return (
    <form className="app-search" role="search" onSubmit={submit}>
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      {/* A real label, visually hidden: a search box labelled only by a magnifying glass is
          unlabelled to anyone not looking at it. */}
      <label className="sr-only" htmlFor="app-search-input">
        Search findings
      </label>
      <input
        id="app-search-input"
        type="search"
        role="combobox"
        placeholder="Search findings, e.g. MFA"
        value={query}
        spellCheck={false}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        onFocus={() => void load()}
        onChange={(event) => {
          setQuery(event.target.value)
          setExpanded(true)
          void load()
        }}
        onKeyDown={onKeyDown}
        // Closing on blur waits for a click on an option to land first; options also stop the
        // mousedown from blurring the box at all.
        onBlur={() => window.setTimeout(() => setExpanded(false), 150)}
      />

      {open ? (
        <ul id={listId} role="listbox" aria-label="Matching findings" className="app-search-results">
          {matches.map((f, i) => (
            <li
              key={f.id}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'app-search-option is-active' : 'app-search-option'}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => openFinding(f)}
            >
              <span className="app-search-option-title">{f.title}</span>
              {/* The summary already opens with the severity ("Critical · 12 vulnerabilities…"),
                  so it stands in for it rather than repeating it. */}
              <span className="app-search-option-meta">
                {f.area} · {f.summary ?? f.severity}
              </span>
            </li>
          ))}
          <li
            id={optionId(matches.length)}
            role="option"
            aria-selected={active === matches.length}
            className={
              active === matches.length
                ? 'app-search-option app-search-all is-active'
                : 'app-search-option app-search-all'
            }
            onMouseDown={(event) => event.preventDefault()}
            onClick={openAll}
          >
            {/* Never "no finding matches": the suggestions look only at the hundred most severe open
                findings, and a failed read looked at none. What is true is that there is nothing to
                suggest, and the full search is one step away. */}
            {matches.length > 0
              ? `See all results for “${query.trim()}”`
              : unavailable
                ? `Suggestions are unavailable — search all findings for “${query.trim()}”`
                : findings === null
                  ? 'Searching…'
                  : `No suggestions — search all findings for “${query.trim()}”`}
          </li>
        </ul>
      ) : null}
    </form>
  )
}
